import React, {useEffect, useState} from 'react';
import { type ProgressMessage } from '../../../../api/courcesService';
import { downloadCertificateFile, getMyCertificates } from '../../../../api/certificateService';
import './CertificateProgress.css';
import { useTranslation } from 'react-i18next';

interface CertificateProgressProps {
  progressPercent?: number;
  isCompleted?: boolean;
  courseTitle?: string;
  progressMessages?: ProgressMessage[];
  onJumpAhead?: () => void;
  courseId?: number; // Add courseId for certificate download
}

const CertificateProgress: React.FC<CertificateProgressProps> = ({
  progressPercent = 8,
  // isCompleted = false,
  courseTitle = "Jasper AI",
  progressMessages = [],
  onJumpAhead,
  courseId
}) => {
  const { t } = useTranslation();

  const [image, setCompImage] = useState('/images/ui/medal.png')
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  // Function to handle certificate download
  const handleDownloadCertificate = async () => {
    if (!courseId) {
      console.error(t('product.certificateProgress.errors.noCourseId'));
      return;
    }

    setIsDownloading(true);
    setDownloadError(null);

    try {
      // First, fetch user's certificates
      const certificatesResponse = await getMyCertificates();
      const certificates = certificatesResponse.data || [];
      console.log('certificates', certificates, courseId)

      // Find the certificate that matches the current course ID
      const matchingCertificate = certificates.find(cert =>
        cert?.course?.id === courseId
      );

      console.log('certificates test', matchingCertificate)

      if (!matchingCertificate) {
        throw new Error(t('product.certificateProgress.errors.notFoundForCourse'));
      }

      // Download the certificate using the certificate ID
      const filename = `${matchingCertificate?.certificateNumber}-certificate.pdf`;
      await downloadCertificateFile(matchingCertificate.id, filename);
    } catch (error: any) {
      console.error('Error downloading certificate:', error);
      setDownloadError(
        error.response?.data?.message ||
        error.message ||
        t('product.certificateProgress.errors.downloadFailed')
      );
    } finally {
      setIsDownloading(false);
    }
  };

  // Determine which medal image to show
  // Function to get the appropriate progress message based on current progress
  const getProgressMessage = (currentProgress: number) => {
    // Use provided progressMessages, fallback to default if not available
    const messages = progressMessages.length > 0 ? progressMessages : [
      {
        id: 1,
        title: t('product.certificateProgress.fallback.title'),
        description: t('product.certificateProgress.fallback.description'),
        percentProgress: 0
      }
    ];

    // Sort messages by percentProgress in descending order to find the highest applicable message
    const sortedMessages = [...messages].sort((a, b) => b.percentProgress - a.percentProgress);

    // Find the first message where current progress is >= the message's percentProgress
    const applicableMessage = sortedMessages.find(message => currentProgress >= message.percentProgress);

    // Return the applicable message or the first message as fallback
    return applicableMessage || messages[0];
  };

  // Set up the progress ring animation
  useEffect(() => {
    const progressRing = document.querySelector('.certificate-progress-ring') as HTMLElement;
    if (progressRing) {
      // Calculate the progress angle for the 270-degree arc
      const progress = Math.min(Math.max(progressPercent, 0), 100);
      if(progress === 100){
        setCompImage('/images/ui/medal-fill.png')
      }
      const maxAngle = 270; // 270 degrees total arc
      const progressAngle = (progress / 100) * maxAngle;

      // Set the CSS custom property for the conic-gradient
      progressRing.setAttribute('data-progress', progress.toString());
      progressRing.style.setProperty('--progress-angle', `${progressAngle}deg`);
    }
  }, [progressPercent]);

  const currentMessage = getProgressMessage(progressPercent);

  return (
    <div className="certificate-progress-container">
      <div className="certificate-progress-card">
        <div className="certificate-progress-content">
          <div className="certificate-progress-visual">
            <div className="certificate-progress-ring">
              <img
                src={image}
                alt={t('product.certificateProgress.imageAlt') || 'Certificate Medal'}
                className="certificate-medal-icon"
              />
            </div>
            <div className="certificate-progress-percentage">
              {progressPercent}%
            </div>
          </div>

          <div className="certificate-progress-text">
            <h3 className="certificate-progress-title">
              {currentMessage.title}
            </h3>
            <p className="certificate-progress-description">
              {currentMessage.description}
            </p>
          </div>
          {image === '/images/ui/medal-fill.png' && (
            <>
              {downloadError && (
                <div className="certificate-download-error">
                  {downloadError}
                </div>
              )}
              <button
                className='certificate-button'
                onClick={handleDownloadCertificate}
                disabled={isDownloading}
              >
                {isDownloading ? t('product.certificateProgress.downloading') : t('product.certificateProgress.getCertificate')}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="certificate-course-info">
        <div className="certificate-course-category">
          {t('product.certificateProgress.categoryPrefix')} • {courseTitle}
        </div>
        <div className="certificate-course-name">
          {courseTitle}
        </div>
      </div>
        <button
          className="certificate-jump-ahead-btn"
          onClick={onJumpAhead}
        >
          {t('product.certificateProgress.jumpAheadCta')}
        </button>
    </div>
  );
};

export default CertificateProgress;
